'use client';

import { useState, useCallback } from 'react';
import { ChevronRight, ChevronLeft, Shield, Phone } from 'lucide-react';

interface WelcomeScreen {
  heading: string;
  icon: typeof Shield;
  body: React.ReactNode;
}

const SCREENS: WelcomeScreen[] = [
  {
    heading: '10DLC & Phone Registration',
    icon: Shield,
    body: (
      <>
        <p className="text-base">
          Before you can send SMS messages, mobile carriers require two things: a
          registered phone number and 10DLC business verification.
        </p>
        <p className="text-[15px]">
          <strong>10DLC</strong> (10-Digit Long Code) is a carrier-mandated program that
          verifies your business identity. It improves message deliverability, protects
          against spam, and ensures your messages reach your clients reliably.
        </p>
        <p className="text-[15px]">
          Don&rsquo;t worry &mdash; Voley handles the entire registration process for you.
          You just provide your information and we take care of the rest.
        </p>
      </>
    ),
  },
  {
    heading: 'How Registration Works',
    icon: Phone,
    body: (
      <>
        <p className="text-base">
          The registration process has two parts:
        </p>
        <div className="flex flex-col gap-3 pl-4">
          <div className="flex items-start gap-3">
            <div className="flex items-center justify-center h-6 w-6 rounded-full bg-accent-blue text-white text-xs font-bold shrink-0 mt-0.5">1</div>
            <div>
              <span className="text-[15px] font-semibold text-gray-900">Phone Number Registration</span>
              <p className="text-sm text-gray-600 mt-0.5">
                Choose how to get your number &mdash; purchase a new one, host an existing
                landline, or port a number from another carrier.
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="flex items-center justify-center h-6 w-6 rounded-full bg-accent-blue text-white text-xs font-bold shrink-0 mt-0.5">2</div>
            <div>
              <span className="text-[15px] font-semibold text-gray-900">10DLC Business Verification</span>
              <p className="text-sm text-gray-600 mt-0.5">
                Provide your business details (legal name, EIN, address) so carriers can
                verify your identity. Review typically takes 5&ndash;10 business days.
              </p>
            </div>
          </div>
        </div>
      </>
    ),
  },
];

interface RegistrationWelcomeModalProps {
  onComplete: () => void;
}

export function RegistrationWelcomeModal({ onComplete }: RegistrationWelcomeModalProps) {
  const [activeScreen, setActiveScreen] = useState(0);
  const isLastScreen = activeScreen === SCREENS.length - 1;

  const handleNext = useCallback(() => {
    if (isLastScreen) {
      onComplete();
    } else {
      setActiveScreen((prev) => prev + 1);
    }
  }, [isLastScreen, onComplete]);

  const handleBack = useCallback(() => {
    setActiveScreen((prev) => Math.max(0, prev - 1));
  }, []);

  const screen = SCREENS[activeScreen];
  const Icon = screen.icon;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-[200] bg-black/50 backdrop-blur-sm animate-in fade-in duration-200" />

      {/* Modal */}
      <div className="fixed inset-0 z-[201] flex items-center justify-center p-4">
        <div
          className="w-full max-w-lg rounded-2xl bg-white shadow-2xl animate-in zoom-in-95 fade-in duration-300"
          role="dialog"
          aria-modal="true"
          aria-label="Registration Tour Welcome"
        >
          {/* Content */}
          <div className="p-8">
            {/* Icon and Heading Row */}
            <div className="flex items-start gap-4 mb-6">
              <div className="flex items-center justify-center h-12 w-12 rounded-xl shrink-0 bg-accent-blue/[0.08]">
                <Icon className="h-6 w-6 text-accent-blue" />
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-bold mb-1 text-gray-900">{screen.heading}</h2>
              </div>
            </div>

            {/* Body */}
            <div className="text-left leading-relaxed mb-8 space-y-4 text-gray-600 ml-16">
              {screen.body}
            </div>

            {/* Navigation */}
            <div className="flex items-center justify-between">
              {activeScreen > 0 ? (
                <button
                  onClick={handleBack}
                  aria-label="Previous screen"
                  className="inline-flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-medium text-gray-500 hover:bg-gray-50 transition-colors"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Back
                </button>
              ) : (
                <div />
              )}

              <button
                onClick={handleNext}
                aria-label={isLastScreen ? 'Start the tour' : 'Next screen'}
                className="inline-flex items-center gap-1.5 px-6 py-2.5 rounded-full text-sm font-medium text-white bg-accent-blue hover:brightness-110 transition-all shadow-sm hover:shadow-md"
              >
                {isLastScreen ? "Let's Go" : 'Next'}
                {!isLastScreen && <ChevronRight className="h-4 w-4" />}
              </button>
            </div>

            {/* Step dots */}
            <div className="flex justify-center gap-1.5 mt-5">
              {SCREENS.map((_, i) => (
                <div
                  key={i}
                  className={`h-1.5 rounded-full transition-all duration-200 ${
                    i === activeScreen ? 'w-6 bg-accent-blue' : 'w-1.5 bg-gray-200'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
